// ============================================================================
// Dénombrement dans une feuille du noyau (moteur v27, refonte lot 5.6 —
// docs/moteur/REFONTE_MOTEUR.md). Fonctions PURES, sans état de feuille.
// ============================================================================
// Une ligne qui écrit « k parmi n » ou des factorielles a des CALCULS DE
// DÉNOMBREMENT à faire, mesurés sur la ligne ÉCRITE (la forme normale les a
// déjà faits) :
//
//   binom  « 5 parmi 49 » à écrire avec les factorielles (ou la formule)      50
//   known  une valeur à connaître : 0 parmi n, 1 parmi n, (n − 1) parmi n,    10
//          n parmi n, et les petites lignes du triangle de Pascal (n ≤ 5)
//   quot   un quotient de factorielles à simplifier (49! ÷ 44!, n! ÷ (n − 2)!, 2 + écart
//          k ÷ k!) : « écart » = nombre de facteurs qui restent
//   fact   la factorielle d'un nombre à calculer (5! = 120)                      2
//
// Les pas du cours : écrire k parmi n avec les factorielles (définition), la
// formule n(n − 1)…(n − k + 1) ÷ k!, une valeur à connaître, simplifier les
// factorielles, calculer une factorielle. « 5 parmi 49 = 1906884 » d'un bond :
// « Montre comment ». « 49! ÷ (5! × 44!) = 1906884 » : « Un calcul à la fois ».
//
// Dans une feuille qui SIMPLIFIE (objectif `simplify`), l'avancée se lit aussi sur
// les factorielles écrites : chaque argument de factorielle (ou de « parmi », lu
// n! ÷ (k! × (n − k)!)) qui n'est pas dans la réponse éloigne d'elle.
// ============================================================================

import * as Q from './kernel/exact';
import { normalize as normalForm, rfRat, rfSimplify, rfToTerm } from './kernel/normal';
import { canon, sub, termKey, type Relation, type Statement, type Term } from './kernel/term';
import type { StepInfo } from './kernel-sheet';

export interface CountDebt {
  /** « k parmi n » à écrire autrement (ni valeur à connaître) */
  binom: number;
  /** « k parmi n » de valeur à connaître (0 ou 1 parmi n, n ≤ 5…) */
  known: number;
  /** quotients de factorielles à simplifier (49! ÷ 44!, k ÷ k!) */
  quot: number;
  /** écart total de ces quotients (facteurs qui restent) */
  span: number;
  /** factorielles d'un nombre à calculer (5!) */
  fact: number;
  /** factorielles écrites (toutes) */
  facts: number;
}

export const COUNT_STEP_IDS = {
  definition: 'counting.binom_def@1',
  formula: 'counting.binom_formula@1',
  knownValue: 'counting.known_value@1',
  quotient: 'counting.fact_quotient@1',
} as const;

export const COUNT_CARD = 'Dénombrement';

export const countZero = (): CountDebt => ({
  binom: 0,
  known: 0,
  quot: 0,
  span: 0,
  fact: 0,
  facts: 0,
});

/** valeur rationnelle d'un terme (forme normale), null sinon */
function ratOf(t: Term): Q.Rat | null {
  const n = normalForm(t);
  return n.ok ? rfRat(rfSimplify(n.rf)) : null;
}
/** clé de la forme normale (deux écritures du même argument : même clé) */
function nfKey(t: Term): string | null {
  const n = normalForm(t);
  return n.ok ? termKey(canon(rfToTerm(rfSimplify(n.rf)))) : null;
}

/** « k parmi n » vaut-il une valeur à connaître ? (k ou n − k vaut 0 ou 1 ; n ≤ 5) */
export function isKnownBinom(t: Extract<Term, { k: 'binom' }>): boolean {
  const small = (q: Q.Rat | null): boolean =>
    q !== null && Q.isInteger(q) && (q.n === 0n || q.n === 1n);
  if (small(ratOf(t.a)) || small(ratOf(sub(t.b, t.a)))) return true;
  const n = ratOf(t.b);
  return n !== null && Q.isInteger(n) && n.n >= 0n && n.n <= 5n && ratOf(t.a) !== null;
}

/** facteurs d'un produit écrit (le produit lui-même s'il n'en est pas un) */
function factorsOf(t: Term): Term[] {
  return t.k === 'mul' ? t.c.flatMap(factorsOf) : [t];
}
const isFact = (t: Term): t is Extract<Term, { k: 'fn' }> => t.k === 'fn' && t.f === 'fact';

/** Les calculs de dénombrement d'un terme écrit. */
export function countDebt(t: Term, acc: CountDebt = countZero()): CountDebt {
  const paired = new Set<Term>();
  walk(t, acc, paired);
  return acc;
}
function walk(t: Term, acc: CountDebt, paired: Set<Term>): void {
  switch (t.k) {
    case 'binom':
      if (isKnownBinom(t)) acc.known++;
      else acc.binom++;
      walk(t.a, acc, paired);
      walk(t.b, acc, paired);
      return;
    case 'add':
    case 'mul':
      t.c.forEach((c) => walk(c, acc, paired));
      return;
    case 'div':
      pairs(t, acc, paired);
      walk(t.a, acc, paired);
      walk(t.b, acc, paired);
      return;
    case 'pow':
      walk(t.a, acc, paired);
      walk(t.b, acc, paired);
      return;
    case 'fn':
      if (isFact(t)) {
        acc.facts++;
        const v = ratOf(t.a);
        if (!paired.has(t) && v !== null && Q.isInteger(v) && v.n >= 0n) acc.fact++;
      }
      walk(t.a, acc, paired);
      return;
    default:
      return;
  }
}
/**
 * Quotients à simplifier : une factorielle du haut et une du bas dont les arguments
 * diffèrent d'un entier (49! ÷ 44!, n! ÷ (n − 2)!), ou un facteur égal à l'argument
 * d'une factorielle de l'autre étage (k ÷ k!, n! ÷ n). Chaque factorielle sert une fois,
 * avec le partenaire le plus proche.
 */
function pairs(t: Extract<Term, { k: 'div' }>, acc: CountDebt, paired: Set<Term>): void {
  const top = factorsOf(t.a);
  const bottom = factorsOf(t.b);
  const used = new Set<Term>();
  const facts = (xs: Term[]): Extract<Term, { k: 'fn' }>[] => xs.filter(isFact);
  // factorielle contre factorielle
  for (const f of facts(top)) {
    let best: { g: Term; d: number } | null = null;
    for (const g of facts(bottom)) {
      if (used.has(g)) continue;
      const d = ratOf(sub(f.a, g.a));
      if (d === null || !Q.isInteger(d)) continue;
      const span = Number(d.n < 0n ? -d.n : d.n);
      if (!best || span < best.d) best = { g, d: span };
    }
    if (best) {
      used.add(best.g);
      used.add(f);
      paired.add(f).add(best.g);
      acc.quot++;
      acc.span += Math.min(best.d, 40);
    }
  }
  // un facteur égal à l'argument d'une factorielle de l'autre étage : k ÷ k!, n! ÷ n
  const absorb = (fs: Term[], others: Term[]): void => {
    for (const f of facts(fs)) {
      if (used.has(f)) continue;
      const key = nfKey(f.a);
      if (key === null || ratOf(f.a) !== null) continue;
      const g = others.find((o) => !isFact(o) && !used.has(o) && nfKey(o) === key);
      if (g) {
        used.add(g);
        used.add(f);
        paired.add(f);
        acc.quot++;
        acc.span += 1;
      }
    }
  };
  absorb(bottom, top);
  absorb(top, bottom);
}

/** Les calculs de dénombrement d'une ligne (tous ses membres). */
export function countDebtOf(s: Statement, relations: Relation[]): CountDebt {
  const acc = countZero();
  if (s.k === 'expr') countDebt(s.e, acc);
  for (const r of relations) {
    countDebt(r.l, acc);
    countDebt(r.r, acc);
  }
  return acc;
}
export const countWeight = (d: CountDebt): number =>
  50 * d.binom + 10 * d.known + 2 * d.quot + d.span + 2 * d.fact;
export const countChanged = (a: CountDebt, b: CountDebt): boolean =>
  (Object.keys(a) as (keyof CountDebt)[]).some((k) => a[k] !== b[k]);

/** une division est-elle écrite ? (la formule n(n − 1)…(n − k + 1) ÷ k! en a une) */
export function hasDivision(t: Term): boolean {
  switch (t.k) {
    case 'div':
      return true;
    case 'add':
    case 'mul':
      return t.c.some(hasDivision);
    case 'pow':
    case 'binom':
      return hasDivision(t.a) || hasDivision(t.b);
    case 'fn':
      return hasDivision(t.a);
    default:
      return false;
  }
}

/**
 * Arguments des factorielles d'un terme : n! donne n ; « k parmi n » donne n, k et
 * n − k (n! ÷ (k! × (n − k)!)). 49! donne « #49 » ; 0! et 1! ne comptent pas.
 */
export function factorialArgs(t: Term, out: Set<string> = new Set()): Set<string> {
  // 0! et 1! valent 1 : ils ne comptent pas
  const add = (a: Term): void => {
    const v = ratOf(a);
    if (v !== null) {
      if (!(Q.isInteger(v) && v.n <= 1n)) out.add(`#${Q.ratToText(v)}`);
      return;
    }
    const k = nfKey(a);
    if (k !== null) out.add(k);
  };
  switch (t.k) {
    case 'binom':
      add(t.b);
      add(t.a);
      add(sub(t.b, t.a));
      factorialArgs(t.a, out);
      factorialArgs(t.b, out);
      break;
    case 'fn':
      if (isFact(t)) add(t.a);
      factorialArgs(t.a, out);
      break;
    case 'add':
    case 'mul':
      t.c.forEach((c) => factorialArgs(c, out));
      break;
    case 'div':
    case 'pow':
      factorialArgs(t.a, out);
      factorialArgs(t.b, out);
      break;
    default:
      break;
  }
  return out;
}

/** Ce que la ligne écrit en plus de la réponse (arguments de factorielles absents d'elle). */
export function extraArgs(line: Term, answer: Term): number {
  const a = factorialArgs(answer);
  let n = 0;
  for (const k of factorialArgs(line)) if (!a.has(k)) n++;
  return n;
}

export type CountStep = { info: StepInfo } | { reject: { title: string; message: string } };

/**
 * Le pas de dénombrement d'une ligne à la suivante (même valeur) : null si rien ne
 * change dans les calculs de dénombrement. `plain` : la ligne d'arrivée n'a plus de
 * calcul à faire (une valeur, ou la réponse écrite).
 */
export function countStep(
  from: CountDebt,
  to: CountDebt,
  toTerm: { division: boolean; plain: boolean },
): CountStep | null {
  if (to.binom > from.binom)
    return {
      info: {
        id: COUNT_STEP_IDS.definition,
        card: COUNT_CARD,
        label: 'Reconnaître k parmi n',
        hora: 2,
        why: 'n! ÷ (k! × (n − k)!) s’écrit « k parmi n » : le nombre de façons de choisir k éléments parmi n.',
      },
    };
  const removed = to.binom + to.known < from.binom + from.known;
  if (removed) {
    if (to.facts > from.facts)
      return {
        info: {
          id: COUNT_STEP_IDS.definition,
          card: COUNT_CARD,
          label: 'Écrire avec les factorielles',
          hora: 2,
          why: 'k parmi n = n! ÷ (k! × (n − k)!).',
        },
      };
    if (toTerm.division)
      return {
        info: {
          id: COUNT_STEP_IDS.formula,
          card: COUNT_CARD,
          label: 'Formule de k parmi n',
          hora: 2,
          why: 'k parmi n = n × (n − 1) × … × (n − k + 1) ÷ k! : k facteurs en haut, k! en bas.',
        },
      };
    // une valeur à connaître (1 parmi n = n, 2 parmi 4 = 6) s'écrit directement
    if (to.binom === from.binom)
      return {
        info: {
          id: COUNT_STEP_IDS.knownValue,
          card: COUNT_CARD,
          label: 'Valeur à connaître',
          hora: 1,
          why: '0 parmi n = 1, 1 parmi n = n, (n − 1) parmi n = n, n parmi n = 1 ; les premières lignes du triangle de Pascal se lisent directement.',
        },
      };
    return {
      reject: {
        title: 'Montre comment',
        message:
          'Ta ligne est juste, mais on ne voit pas le calcul : écris d’abord « k parmi n » avec les factorielles, n! ÷ (k! × (n − k)!), ou avec la formule n × (n − 1) × … ÷ k!.',
      },
    };
  }
  if (to.quot < from.quot || to.span < from.span) {
    // 49! ÷ (5! × 44!) = 1906884, 100! ÷ 98! = 9900 : simplifier ET calculer (un seul
    // facteur qui reste, n! ÷ (n − 1)! = n, s'écrit directement)
    if (toTerm.plain && from.span - to.span >= 2)
      return {
        reject: {
          title: 'Un calcul à la fois',
          message:
            'Ta ligne est juste, mais elle simplifie les factorielles ET calcule d’un coup. Écris d’abord le quotient simplifié (100! ÷ 98! = 100 × 99), puis calcule.',
        },
      };
    return {
      info: {
        id: COUNT_STEP_IDS.quotient,
        card: COUNT_CARD,
        label: 'Simplifier les factorielles',
        hora: 2,
        why: 'n! = n × (n − 1) × … × (p + 1) × p! : on simplifie le quotient par p! (100! ÷ 98! = 100 × 99).',
      },
    };
  }
  if (to.fact < from.fact)
    return {
      info: {
        id: 'kernel.compute@1',
        card: COUNT_CARD,
        label: 'Calculer la factorielle',
        hora: 1,
        why: 'n! = n × (n − 1) × … × 2 × 1 : 5! = 120.',
      },
    };
  return null;
}
